import { useRef, useState } from "react";
import { endpoints, formatApiError } from "../lib/api";
import { computeTM } from "../lib/tmEngine";
import { formatCurrency, formatCurrencyExact } from "../lib/romEngine";
import {
    FileXls, FilePdf, UploadSimple, CircleNotch, CheckCircle, WarningCircle,
    X, ArrowRight, Sparkle,
} from "@phosphor-icons/react";

const LOSS_TYPES = ["water", "fire", "mould", "storm"];

function Backdrop({ children, onClose, wide }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
            <div className={`rom-card w-full ${wide ? "max-w-4xl" : "max-w-2xl"} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
                {children}
            </div>
        </div>
    );
}

function Dropzone({ onPick, loading, label, hint, testid }) {
    return (
        <button
            onClick={onPick}
            disabled={loading}
            data-testid={testid}
            className="w-full border-2 border-dashed border-[var(--rom-border)] rounded-[8px] py-10 flex flex-col items-center gap-2 hover:border-[var(--rom-orange)] hover:bg-orange-50/40 transition-colors"
        >
            {loading
                ? <><CircleNotch size={28} className="animate-spin text-[var(--rom-orange)]" /><span className="text-sm text-[var(--rom-text-2)]">{hint}</span></>
                : <><UploadSimple size={28} weight="bold" className="text-[var(--rom-text-3)]" /><span className="text-sm font-semibold">{label}</span><span className="text-[11px] text-[var(--rom-text-3)]">Select one or more files</span></>}
        </button>
    );
}

/* ----------------------------------------------------------------------- */
/* ROM (Excel) batch importer                                              */
/* ----------------------------------------------------------------------- */
export function RomImportModal({ open, onClose, clients, onImported }) {
    const fileRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [rows, setRows] = useState(null);     // [{filename, ok, preview, error}]
    const [edits, setEdits] = useState({});      // index -> {name, loss}
    const [clientId, setClientId] = useState("");
    const [saving, setSaving] = useState(false);
    const [progress, setProgress] = useState(0);

    if (!open) return null;

    function reset() { setRows(null); setEdits({}); setClientId(""); setError(""); setProgress(0); }

    async function handleFiles(e) {
        const files = [...(e.target.files || [])];
        if (!files.length) return;
        setError(""); setLoading(true); setRows(null);
        try {
            const fd = new FormData();
            files.forEach((f) => fd.append("files", f));
            const { data } = await endpoints.importRomBatch(fd);
            const ed = {};
            data.results.forEach((r, i) => {
                if (r.ok) ed[i] = { name: r.preview.project_name || "", loss: r.preview.loss_type || "water" };
            });
            setRows(data.results);
            setEdits(ed);
        } catch (err) {
            setError(formatApiError(err));
        }
        setLoading(false);
    }

    const okCount = (rows || []).filter((r) => r.ok).length;

    async function saveAll() {
        if (!rows) return;
        setSaving(true); setError(""); setProgress(0);
        let done = 0;
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            if (!r.ok) continue;
            const ed = edits[i] || {};
            const tm = r.preview.tm;
            const totals = computeTM(tm);
            const job = {
                ref: r.preview.ref,
                loss_type: ed.loss || "water",
                client_id: clientId || null,
                address: "",
                status: "approved",
                imported: true,
                import_source: "excel",
                import_filename: r.preview.import_filename,
                inputs: { mode: "tm", tm, projectName: ed.name, consumablesPct: tm.consumablesPct },
                result: {
                    mode: "tm",
                    grand_total: totals.grandTotal, grandTotal: totals.grandTotal,
                    labour: totals.labour, consumables: totals.consumables,
                    equipment: totals.equipment, subs: totals.subs, misc: totals.misc,
                },
            };
            try {
                await endpoints.saveJob(job);
            } catch (err) {
                setError(`${r.filename}: ${formatApiError(err)}`);
            }
            done += 1;
            setProgress(done);
        }
        setSaving(false);
        onImported?.();
        onClose();
        reset();
    }

    return (
        <Backdrop wide={!!rows} onClose={() => { onClose(); reset(); }}>
            <div className="rom-card-head flex items-center justify-between">
                <h3 className="flex items-center gap-2"><FileXls size={18} weight="bold" className="text-[var(--rom-green)]" /> Import ROMs from Excel</h3>
                <button onClick={() => { onClose(); reset(); }} className="text-[var(--rom-text-3)] hover:text-[var(--rom-text)]" data-testid="rom-import-close"><X size={18} /></button>
            </div>
            <div className="rom-card-body space-y-4">
                {!rows && (
                    <Dropzone onPick={() => fileRef.current?.click()} loading={loading}
                        label="Choose SERVPRO ROM workbooks (.xlsx)" hint="Parsing workbooks…" testid="rom-import-dropzone" />
                )}
                <input ref={fileRef} type="file" accept=".xlsx,.xlsm" multiple className="hidden" onChange={handleFiles} data-testid="rom-import-file" />

                {error && <div className="text-sm text-[var(--rom-danger)] flex items-center gap-2" data-testid="rom-import-error"><WarningCircle size={16} /> {error}</div>}

                {rows && (
                    <div className="space-y-4" data-testid="rom-import-preview">
                        <div className="flex items-center justify-between gap-3">
                            <span className="text-sm text-[var(--rom-text-2)]">{okCount} of {rows.length} workbook{rows.length > 1 ? "s" : ""} parsed</span>
                            <label className="flex items-center gap-2 text-xs">
                                <span className="text-[var(--rom-text-3)] uppercase tracking-wider font-semibold">Client (all)</span>
                                <select className="input py-1" value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="rom-import-client">
                                    <option value="">— none —</option>
                                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </label>
                        </div>

                        <div className="border border-[var(--rom-border)] rounded-[8px] overflow-hidden">
                            <table className="rom-table text-sm">
                                <thead><tr>
                                    <th>File</th><th>Project name</th><th>Loss</th><th className="numeric">Total</th>
                                </tr></thead>
                                <tbody>
                                    {rows.map((r, i) => r.ok ? (
                                        <tr key={i} data-testid={`rom-batch-row-${i}`}>
                                            <td className="text-xs text-[var(--rom-text-3)] max-w-[160px] truncate">{r.filename}</td>
                                            <td><input className="input py-1" value={edits[i]?.name || ""} onChange={(e) => setEdits({ ...edits, [i]: { ...edits[i], name: e.target.value } })} data-testid={`rom-batch-name-${i}`} /></td>
                                            <td><select className="input py-1 capitalize" value={edits[i]?.loss || "water"} onChange={(e) => setEdits({ ...edits, [i]: { ...edits[i], loss: e.target.value } })} data-testid={`rom-batch-loss-${i}`}>{LOSS_TYPES.map((l) => <option key={l} value={l}>{l}</option>)}</select></td>
                                            <td className="numeric font-semibold">{formatCurrency(computeTM(r.preview.tm).grandTotal)}</td>
                                        </tr>
                                    ) : (
                                        <tr key={i} className="bg-red-50/50">
                                            <td className="text-xs max-w-[160px] truncate">{r.filename}</td>
                                            <td colSpan={3} className="text-xs text-[var(--rom-danger)] flex items-center gap-1"><WarningCircle size={12} /> {r.error}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex justify-end gap-2">
                            <button className="btn btn-ghost" onClick={reset}>Choose other files</button>
                            <button className="btn btn-primary" onClick={saveAll} disabled={saving || okCount === 0} data-testid="rom-import-save">
                                {saving ? <><CircleNotch size={14} className="animate-spin" /> Saving {progress}/{okCount}</> : <><CheckCircle size={14} weight="bold" /> Import {okCount} to repository</>}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </Backdrop>
    );
}

/* ----------------------------------------------------------------------- */
/* Invoice (PDF) batch importer                                            */
/* ----------------------------------------------------------------------- */
export function InvoiceImportModal({ open, onClose, onApplied, lockedJob = null }) {
    const fileRef = useRef(null);
    const pollRef = useRef({ cancelled: false });
    const [loading, setLoading] = useState(false);
    const [loadMsg, setLoadMsg] = useState("");
    const [loadStat, setLoadStat] = useState({ completed: 0, total: 0 });
    const [error, setError] = useState("");
    const [rows, setRows] = useState(null);      // [{filename, ok, parsed, error}]
    const [candidates, setCandidates] = useState([]);
    const [targets, setTargets] = useState({});   // index -> jobId
    const [saving, setSaving] = useState(false);
    const [progress, setProgress] = useState(0);
    const [noSync, setNoSync] = useState({});   // index -> true when user opts OUT of sqft sync

    if (!open) return null;

    function reset() {
        pollRef.current.cancelled = true;
        setRows(null); setCandidates([]); setTargets({}); setError(""); setProgress(0);
        setLoading(false); setLoadMsg(""); setLoadStat({ completed: 0, total: 0 });
    }
    function closeAll() { onClose(); reset(); }

    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    async function handleFiles(e) {
        const files = [...(e.target.files || [])];
        if (!files.length) return;
        setError(""); setLoading(true); setRows(null); setLoadMsg("Uploading invoices…");
        setLoadStat({ completed: 0, total: files.length });
        pollRef.current = { cancelled: false };
        try {
            const fd = new FormData();
            files.forEach((f) => fd.append("files", f));
            const { data } = await endpoints.startInvoiceImport(fd);
            setCandidates(data.candidates || []);
            await pollStatus(data.task_id, data.total || files.length);
        } catch (err) {
            setError(formatApiError(err));
            setLoading(false);
        }
        if (fileRef.current) fileRef.current.value = "";
    }

    async function pollStatus(taskId, total) {
        let misses = 0;
        while (!pollRef.current.cancelled) {
            let doc;
            try {
                const res = await endpoints.invoiceImportStatus(taskId);
                doc = res.data; misses = 0;
            } catch {
                if (++misses > 10) { setError("Lost connection while reading invoices."); setLoading(false); return; }
                await sleep(2500); continue;
            }
            setLoadMsg(`Reading invoices with AI… ${doc.completed || 0}/${doc.total ?? total}`);
            setLoadStat({ completed: doc.completed || 0, total: doc.total ?? total });
            if (doc.status === "done" || doc.status === "error") {
                if (doc.status === "error") setError(doc.error || "Invoice processing failed.");
                const results = doc.results || [];
                const tg = {};
                results.forEach((r, i) => {
                    if (r.ok) tg[i] = lockedJob ? lockedJob.id : (r.parsed.matched_job_id || "");
                });
                setRows(results);
                setCandidates(doc.candidates || []);
                setTargets(tg);
                setLoading(false);
                return;
            }
            await sleep(2500);
        }
    }

    const cmap = Object.fromEntries(candidates.map((c) => [c.id, c]));
    if (lockedJob && !cmap[lockedJob.id]) cmap[lockedJob.id] = lockedJob;
    function netActual(p) {
        if (p.tier === "B") return p.line_item_total || p.pre_tax_total;
        return Math.max(0, p.invoice_total - p.tax);
    }
    function variance(i, p) {
        const rom = cmap[targets[i]]?.grand_total || 0;
        if (!rom) return null;
        return ((netActual(p) - rom) / rom) * 100;
    }
    const applyable = (rows || []).filter((r, i) => r.ok && targets[i] && !r.parsed.parse_incomplete).length;

    async function applyAll() {
        if (!rows) return;
        setSaving(true); setError(""); setProgress(0);
        let done = 0;
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            if (!r.ok || !targets[i] || r.parsed.parse_incomplete) continue;
            const p = r.parsed;
            const romSqft = cmap[targets[i]]?.sqft || 0;
            const doSync = p.square_footage > 0 && !noSync[i] && Math.abs((p.square_footage || 0) - romSqft) > 1;
            const payload = {
                actual_cost: netActual(p),
                tier: p.tier,
                breakdown: p.tier === "B" ? p.breakdown : {},
                invoice_total: p.invoice_total, tax: p.tax,
                overhead_profit: p.overhead_profit, line_item_total: p.line_item_total,
                net_claim: p.net_claim, replacement_cost_value: p.replacement_cost_value,
                subtotal: p.subtotal, material_sales_tax: p.material_sales_tax,
                cleaning_sales_tax: p.cleaning_sales_tax, summary_source: p.summary_source,
                square_footage: p.square_footage, total_area: p.total_area,
                interior_wall_area: p.interior_wall_area, stories: p.stories, room_count: p.room_count,
                set_sqft: doSync ? p.square_footage : undefined,
                doc_type: p.doc_type, doc_type_reason: p.doc_type_reason,
                reconciliation: p.reconciliation, flags: p.flags,
                calibration_eligible: p.calibration_eligible,
                source: "invoice_import", invoice_filename: p.invoice_filename, invoice_number: p.invoice_number,
            };
            try {
                await endpoints.recordActual(targets[i], payload);
            } catch (err) {
                setError(`${r.filename}: ${formatApiError(err)}`);
            }
            done += 1;
            setProgress(done);
        }
        setSaving(false);
        onApplied?.();
        closeAll();
    }

    return (
        <Backdrop wide={!!rows} onClose={closeAll}>
            <div className="rom-card-head flex items-center justify-between">
                <h3 className="flex items-center gap-2"><FilePdf size={18} weight="bold" className="text-[var(--rom-orange)]" /> Import Invoice{lockedJob ? "" : "s"} <span className="chip chip-orange"><Sparkle size={10} weight="bold" /> AI</span></h3>
                <button onClick={closeAll} className="text-[var(--rom-text-3)] hover:text-[var(--rom-text)]" data-testid="invoice-import-close"><X size={18} /></button>
            </div>
            <div className="rom-card-body space-y-4">
                {lockedJob && (
                    <div className="text-xs text-[var(--rom-text-2)] bg-slate-50 border border-[var(--rom-border)] rounded-[6px] px-3 py-2" data-testid="invoice-import-locked">
                        Recording actuals against <b>{lockedJob.ref}</b>{lockedJob.project_name && lockedJob.project_name !== lockedJob.ref ? ` · ${lockedJob.project_name}` : ""} · ROM {formatCurrency(lockedJob.grand_total)}
                    </div>
                )}
                {!rows && (
                    <Dropzone onPick={() => fileRef.current?.click()} loading={loading}
                        label={lockedJob ? "Choose the final invoice (PDF)" : "Choose final invoices (PDF)"} hint={loadMsg || "Reading invoices with AI…"} testid="invoice-import-dropzone" />
                )}
                <input ref={fileRef} type="file" accept=".pdf" multiple={!lockedJob} className="hidden" onChange={handleFiles} data-testid="invoice-import-file" />

                {loading && loadStat.total > 0 && (
                    <div className="space-y-1.5" data-testid="invoice-import-progress">
                        <div className="flex items-center justify-between text-[11px] font-semibold text-[var(--rom-text-2)]">
                            <span className="flex items-center gap-1.5"><Sparkle size={11} weight="bold" className="text-[var(--rom-orange)]" /> {loadMsg || "Reading invoices with AI…"}</span>
                            <span className="font-mono" data-testid="invoice-import-progress-count">{loadStat.completed} of {loadStat.total} parsed</span>
                        </div>
                        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full rounded-full bg-[var(--rom-orange)] transition-[width] duration-500 ease-out"
                                style={{ width: `${Math.max(6, (loadStat.completed / Math.max(1, loadStat.total)) * 100)}%` }}
                                data-testid="invoice-import-progress-bar" />
                        </div>
                    </div>
                )}

                {error && <div className="text-sm text-[var(--rom-danger)] flex items-center gap-2" data-testid="invoice-import-error"><WarningCircle size={16} /> {error}</div>}

                {rows && (
                    <div className="space-y-4" data-testid="invoice-import-preview">
                        <span className="text-sm text-[var(--rom-text-2)]">{rows.filter((r) => r.ok).length} of {rows.length} invoice{rows.length > 1 ? "s" : ""} read · {lockedJob ? "review, then record" : "match each to a ROM, then record"}</span>

                        <div className="space-y-3">
                            {rows.map((r, i) => !r.ok ? (
                                <div key={i} className="bg-red-50/50 border border-[var(--rom-border)] rounded-[8px] p-3 text-xs text-[var(--rom-danger)] flex items-center gap-2">
                                    <WarningCircle size={14} /> <b className="truncate">{r.filename}</b> — {r.error}
                                </div>
                            ) : (
                                <div key={i} className="border border-[var(--rom-border)] rounded-[8px] p-3 space-y-2" data-testid={`invoice-batch-row-${i}`}>
                                    <div className="flex items-center justify-between gap-2 text-sm">
                                        <span className="font-semibold truncate">{r.parsed.project_name || r.filename}</span>
                                        <div className="flex items-center gap-1.5">
                                            {r.parsed.ocr && <span className="chip chip-navy" title="Read from a scanned/image PDF via AI vision"><Sparkle size={10} weight="bold" /> OCR</span>}
                                            {r.parsed.doc_type === "xactimate_rcv" && <span className="chip chip-gray" title={r.parsed.doc_type_reason || "Xactimate RCV estimate"}>Xactimate RCV</span>}
                                            {r.parsed.doc_type === "tm_actual" && <span className="chip chip-green" title={r.parsed.doc_type_reason || "T&M actual"}>T&amp;M actual</span>}
                                            <span className={`chip ${r.parsed.tier === "B" ? "chip-green" : "chip-gray"}`}>Tier {r.parsed.tier}</span>
                                        </div>
                                    </div>
                                    <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-center">
                                        {lockedJob ? (
                                            <div className="text-sm font-semibold flex items-center gap-2" data-testid={`invoice-batch-target-${i}`}>
                                                <span className="chip chip-navy">{lockedJob.ref}</span> {lockedJob.project_name !== lockedJob.ref ? lockedJob.project_name : ""}
                                            </div>
                                        ) : (
                                            <select className="input py-1" value={targets[i] || ""} onChange={(e) => setTargets({ ...targets, [i]: e.target.value })} data-testid={`invoice-batch-target-${i}`}>
                                                <option value="">— match to ROM —</option>
                                                {candidates.map((c) => <option key={c.id} value={c.id}>{c.ref} · {c.project_name} · {formatCurrency(c.grand_total)}</option>)}
                                            </select>
                                        )}
                                        <div className="flex items-center gap-2 text-sm justify-end">
                                            <span className="text-[var(--rom-text-3)]">{formatCurrency(netActual(r.parsed))}</span>
                                            {variance(i, r.parsed) != null && (
                                                <span className={`font-bold ${variance(i, r.parsed) > 0 ? "text-[var(--rom-orange)]" : "text-[var(--rom-green)]"}`}>
                                                    {variance(i, r.parsed) > 0 ? "+" : ""}{variance(i, r.parsed).toFixed(1)}%
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {!lockedJob && r.parsed.matched_job_id && (
                                        <div className="text-[11px] text-[var(--rom-text-3)] flex items-center gap-1">
                                            <Sparkle size={11} weight="bold" className="text-[var(--rom-orange)]" /> AI {Math.round(r.parsed.match_confidence * 100)}%: {r.parsed.match_reason}
                                        </div>
                                    )}
                                    {r.parsed.parse_incomplete && (
                                        <div className="text-[11px] bg-red-50 border border-red-300 rounded-[6px] px-2 py-1.5 text-red-900" data-testid={`invoice-parse-incomplete-${i}`}>
                                            <div className="font-bold flex items-center gap-1"><WarningCircle size={13} weight="fill" /> Parse incomplete — totals withheld</div>
                                            <div className="mt-0.5">{r.parsed.parse_incomplete_reason}</div>
                                            {r.parsed.multiple_summaries_found && (r.parsed.summaries || []).length > 0 && (
                                                <ul className="mt-1.5 space-y-0.5" data-testid={`invoice-summaries-${i}`}>
                                                    {r.parsed.summaries.map((s, si) => (
                                                        <li key={si} className="flex items-center justify-between gap-2">
                                                            <span className="font-medium">{s.label || `Summary ${si + 1}`}</span>
                                                            <span className="tabular">Net Claim {formatCurrency(s.net_claim)} · LIT {formatCurrency(s.line_item_total)} · O&amp;P {s.overhead != null || s.profit != null ? formatCurrency((s.overhead || 0) + (s.profit || 0)) : "—"}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    )}
                                    {(r.parsed.square_footage > 0 || r.parsed.stories || r.parsed.room_count) && (
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[var(--rom-text-2)]" data-testid={`invoice-footprint-${i}`}>
                                            {r.parsed.square_footage > 0 && <span><b className="text-[var(--rom-text)]">{r.parsed.square_footage.toLocaleString()}</b> SF floor area</span>}
                                            {r.parsed.stories ? <span>· {r.parsed.stories} {r.parsed.stories === 1 ? "story" : "stories"}</span> : null}
                                            {r.parsed.room_count ? <span>· {r.parsed.room_count} rooms</span> : null}
                                            {(() => {
                                                const romSqft = cmap[targets[i]]?.sqft || 0;
                                                const sf = r.parsed.square_footage || 0;
                                                if (!sf || !targets[i] || Math.abs(sf - romSqft) <= 1) return null;
                                                return (
                                                    <label className="flex items-center gap-1 cursor-pointer text-[var(--rom-navy)]" data-testid={`invoice-sync-sqft-${i}`}>
                                                        <input type="checkbox" checked={!noSync[i]} onChange={(e) => setNoSync({ ...noSync, [i]: !e.target.checked })} />
                                                        Set ROM sq ft → {sf.toLocaleString()} {romSqft ? `(was ${romSqft.toLocaleString()})` : ""}
                                                    </label>
                                                );
                                            })()}
                                        </div>
                                    )}
                                    {r.parsed.tier === "B" && <BreakdownPanel parsed={r.parsed} rom={cmap[targets[i]]?.rom_breakdown} testid={`invoice-breakdown-${i}`} />}
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-end gap-2">
                            <button className="btn btn-ghost" onClick={reset}>Choose other files</button>
                            <button className="btn btn-primary" onClick={applyAll} disabled={saving || applyable === 0} data-testid="invoice-import-apply">
                                {saving ? <><CircleNotch size={14} className="animate-spin" /> Recording {progress}/{applyable}</> : <><CheckCircle size={14} weight="bold" /> Record {applyable} actual{applyable !== 1 ? "s" : ""} &amp; learn</>}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </Backdrop>
    );
}

const AE_ROWS = [
    ["labour", "A · Field Labour"],
    ["supervision", "A · PM / Supervision"],
    ["consumables", "B · Consumables"],
    ["equipment", "C · Equipment"],
    ["subcontractors", "D · Subcontractors"],
    ["miscellaneous", "E · Misc / Incidentals"],
];

function BreakdownPanel({ parsed, rom, testid }) {
    const bd = parsed.breakdown || {};
    const rec = parsed.reconciliation || {};
    const total = AE_ROWS.reduce((s, [k]) => s + (bd[k] || 0), 0) || 1;
    const isRCV = parsed.doc_type === "xactimate_rcv";
    return (
        <div className="mt-2 border-t border-[var(--rom-border)] pt-2 space-y-2" data-testid={testid}>
            <table className="w-full text-[11px]">
                <thead>
                    <tr className="text-[var(--rom-text-3)] uppercase tracking-wider">
                        <th className="text-left font-semibold py-0.5">Heading</th>
                        <th className="text-right font-semibold">Actual</th>
                        <th className="text-right font-semibold">%</th>
                        {rom && <th className="text-right font-semibold">ROM</th>}
                        {rom && <th className="text-right font-semibold">Δ</th>}
                    </tr>
                </thead>
                <tbody>
                    {AE_ROWS.map(([k, label]) => {
                        const v = bd[k] || 0;
                        const r = rom ? (rom[k] || 0) : null;
                        const delta = r != null && r > 0 ? ((v - r) / r) * 100 : null;
                        return (
                            <tr key={k} data-testid={`${testid}-${k}`}>
                                <td className="py-0.5 text-[var(--rom-text-2)]">{label}</td>
                                <td className="text-right tabular font-medium">{formatCurrency(v)}</td>
                                <td className="text-right tabular text-[var(--rom-text-3)]">{Math.round((v / total) * 100)}%</td>
                                {rom && <td className="text-right tabular text-[var(--rom-text-3)]">{r != null ? formatCurrency(r) : "—"}</td>}
                                {rom && <td className={`text-right tabular font-bold ${delta == null ? "text-[var(--rom-text-3)]" : delta > 0 ? "text-[var(--rom-orange)]" : "text-[var(--rom-green)]"}`}>
                                    {delta == null ? "—" : `${delta > 0 ? "+" : ""}${delta.toFixed(0)}%`}
                                </td>}
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
                {rec.matches === true && (
                    <span className="text-[var(--rom-green)] flex items-center gap-1" data-testid={`${testid}-reconciled`}>
                        <CheckCircle size={12} weight="fill" /> Ties to line-item total {formatCurrency(rec.line_item_total)}
                    </span>
                )}
                {rec.matches === false && (
                    <span className="text-[var(--rom-danger)] flex items-center gap-1" data-testid={`${testid}-mismatch`}>
                        <WarningCircle size={12} weight="fill" /> Off by {formatCurrency(Math.abs(rec.difference || 0))} vs line-item total {formatCurrency(rec.line_item_total)}
                    </span>
                )}
                {parsed.line_item_total != null && <span className="text-[var(--rom-text-3)]" data-testid={`${testid}-lit`}>Line item {formatCurrencyExact(parsed.line_item_total)}</span>}
                {parsed.overhead_profit != null && parsed.overhead_profit > 0 && <span className="text-[var(--rom-text-3)]">O&amp;P {formatCurrencyExact(parsed.overhead_profit)}</span>}
                {parsed.tax != null && parsed.tax > 0 && <span className="text-[var(--rom-text-3)]">Tax {formatCurrencyExact(parsed.tax)}</span>}
                {parsed.replacement_cost_value != null && parsed.replacement_cost_value > 0 && <span className="text-[var(--rom-text-3)]">RCV {formatCurrencyExact(parsed.replacement_cost_value)}</span>}
                {parsed.net_claim != null && parsed.net_claim > 0
                    ? <span className="text-[var(--rom-text-2)] font-medium" data-testid={`${testid}-net-claim`}>Net claim {formatCurrencyExact(parsed.net_claim)}</span>
                    : parsed.parse_incomplete && <span className="text-[var(--rom-danger)] font-medium" data-testid={`${testid}-net-claim-withheld`}>Net claim withheld</span>}
                {parsed.summary_source === "summary_block" && <span className="chip chip-green" title="Read verbatim from the document's Summary block (not summed)">summary-read</span>}
            </div>

            {parsed.breakdown_incomplete && (
                <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-[4px] px-2 py-1.5" data-testid={`${testid}-breakdown-approximate`}>
                    <b>A–E split is approximate.</b> Net Claim, O&amp;P and tax are read verbatim from the document summary and are reliable; the line-item classification didn&apos;t fully reconcile to the Line Item Total.
                </div>
            )}

            {isRCV && (
                <div className="text-[11px] text-[var(--rom-text-2)] bg-amber-50/60 border border-amber-200 rounded-[4px] px-2 py-1.5" data-testid={`${testid}-rcv-note`}>
                    <b>Xactimate RCV</b> — reference for room/equipment composition only. Recorded against the job, but <b>not</b> used as a dollar-accuracy check in calibration.
                </div>
            )}

            {(parsed.flags || []).length > 0 && (
                <div className="text-[11px] text-[var(--rom-text-2)]" data-testid={`${testid}-flags`}>
                    <div className="font-semibold text-[var(--rom-text-3)] uppercase tracking-wider mb-0.5">Judgment calls</div>
                    <ul className="list-disc pl-4 space-y-0.5">
                        {parsed.flags.map((f, idx) => <li key={idx}>{f}</li>)}
                    </ul>
                </div>
            )}
        </div>
    );
}

